(() => {
  'use strict';

  const CONTROL_SELECTOR = [
    '.slider-button--prev',
    '.mf-ugc-arrow--prev',
    '[data-mf-related-prev]',
    '.ks-arrow--prev',
    '.ls-arrow--prev',
    '.mf-about-arrow--prev',
    '.mf-media__arrow--prev',
    '.mf-banner-slider__arrow--prev',
    '.hero-mod-slider__arrow--prev',
    '.slider-arrow.left',
    '.nav--prev',
    '[data-prev]'
  ].join(',');

  const EXCLUDED_SELECTOR = [
    'cart-drawer',
    '.mf-side-nav',
    '.mf-search',
    '.mf-ugc-modal',
    'nav',
    '[role="dialog"]'
  ].join(',');

  const arrowSvg = (direction) => direction < 0
    ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 8 12l7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) !== 0;
  };

  const hasVisibleExistingControls = (scroller) => {
    const regions = [scroller.parentElement, scroller.parentElement?.parentElement].filter(Boolean);
    return regions.some((region) => Array.from(region.querySelectorAll(CONTROL_SELECTOR)).some(isVisible));
  };

  const hasHorizontalOverflow = (scroller) => (
    scroller.clientWidth > 0 && scroller.scrollWidth - scroller.clientWidth > 8
  );

  const getScrollStep = (scroller) => {
    const children = Array.from(scroller.children).filter((child) => {
      const style = window.getComputedStyle(child);
      return style.display !== 'none' && child.getBoundingClientRect().width > 20;
    });
    const first = children[0];
    if (first) {
      const styles = window.getComputedStyle(scroller);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return Math.max(80, first.getBoundingClientRect().width + gap);
    }
    return Math.max(180, scroller.clientWidth * 0.82);
  };

  const createButton = (direction, tableMode = false) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = tableMode ? 'mf-table-scroll-arrow' : 'mf-scroll-arrow';
    button.dataset.mfScrollDirection = String(direction);
    button.setAttribute('aria-label', direction < 0 ? 'Ver itens anteriores' : 'Ver próximos itens');
    button.innerHTML = arrowSvg(direction);
    return button;
  };

  const enhanceScroller = (scroller, options = {}) => {
    if (!scroller || scroller.dataset.mfScrollEnhanced === 'true') return;
    if (scroller.closest(EXCLUDED_SELECTOR)) return;

    const style = window.getComputedStyle(scroller);
    const overflowEligible = ['auto', 'scroll'].includes(style.overflowX);

    if (!options.force && !overflowEligible) return;
    if (!options.force && (scroller.clientHeight < 38 || scroller.clientWidth < 120)) return;
    if (!hasHorizontalOverflow(scroller)) return;
    if (!options.ignoreExisting && hasVisibleExistingControls(scroller)) {
      scroller.dataset.mfScrollEnhanced = 'native';
      return;
    }

    const host = options.host || scroller.parentElement;
    if (!host) return;

    host.classList.add('mf-scroll-controls-host');
    scroller.dataset.mfScrollEnhanced = 'true';

    const previous = createButton(-1, Boolean(options.tableMode));
    const next = createButton(1, Boolean(options.tableMode));
    previous.dataset.mfScrollFor = 'previous';
    next.dataset.mfScrollFor = 'next';
    host.append(previous, next);

    const positionControls = () => {
      if (!scroller.isConnected || !host.isConnected) return;
      const scrollerRect = scroller.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      const top = scrollerRect.top - hostRect.top + (scrollerRect.height / 2) + host.scrollTop;
      const left = Math.max(5, scrollerRect.left - hostRect.left + 7 + host.scrollLeft);
      const right = Math.max(5, hostRect.right - scrollerRect.right + 7 - host.scrollLeft);

      previous.style.top = `${top}px`;
      previous.style.left = `${left}px`;
      previous.style.right = 'auto';
      next.style.top = `${top}px`;
      next.style.right = `${right}px`;
      next.style.left = 'auto';
    };

    const update = () => {
      const overflow = hasHorizontalOverflow(scroller);
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      previous.hidden = !overflow;
      next.hidden = !overflow;
      previous.disabled = !overflow || scroller.scrollLeft <= 3;
      next.disabled = !overflow || scroller.scrollLeft >= maxScroll - 3;
      positionControls();
    };

    const move = (direction) => {
      scroller.scrollBy({
        left: getScrollStep(scroller) * direction,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));

    let frame = 0;
    scroller.addEventListener('scroll', () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    }, { passive: true });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(update);
      observer.observe(scroller);
      observer.observe(host);
    } else {
      window.addEventListener('resize', update, { passive: true });
    }

    update();
  };

  const prepareDescriptionTables = (scope = document) => {
    scope.querySelectorAll('.product__description-section .mf-specs .mf-table').forEach((table) => {
      if (table.closest('.mf-mobile-table-shell')) return;

      const shell = document.createElement('div');
      const viewport = document.createElement('div');
      shell.className = 'mf-mobile-table-shell';
      viewport.className = 'mf-mobile-table-scroll';
      viewport.tabIndex = 0;
      viewport.setAttribute('role', 'region');
      viewport.setAttribute('aria-label', 'Tabela de medidas. Arraste para o lado para ver todas as informações.');

      table.parentNode.insertBefore(shell, table);
      shell.appendChild(viewport);
      viewport.appendChild(table);

      window.requestAnimationFrame(() => enhanceScroller(viewport, {
        force: true,
        ignoreExisting: true,
        tableMode: true,
        host: shell
      }));
    });
  };

  const scanHorizontalAreas = (scope = document) => {
    const main = scope.matches?.('#MainContent') ? scope : (scope.querySelector?.('#MainContent') || document.getElementById('MainContent'));
    if (!main) return;

    prepareDescriptionTables(scope === document ? main : scope);

    const candidates = main.querySelectorAll('*');
    candidates.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (element.dataset.mfScrollEnhanced) return;
      if (element.closest(EXCLUDED_SELECTOR)) return;
      if (!hasHorizontalOverflow(element)) return;

      const style = window.getComputedStyle(element);
      const eligible = ['auto', 'scroll'].includes(style.overflowX);

      if (!eligible) return;
      enhanceScroller(element);
    });
  };

  let scheduled = false;
  const scheduleScan = (scope = document) => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      scanHorizontalAreas(scope);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleScan(document), { once: true });
  } else {
    scheduleScan(document);
  }

  window.addEventListener('load', () => scheduleScan(document), { once: true });
  window.addEventListener('resize', () => scheduleScan(document), { passive: true });
  document.addEventListener('shopify:section:load', (event) => scheduleScan(event.target));

  const main = document.getElementById('MainContent');
  if (main && 'MutationObserver' in window) {
    const observer = new MutationObserver(() => scheduleScan(main));
    observer.observe(main, { childList: true, subtree: true });
  }
})();
